import { Box, Typography } from "@mui/material";
import { useState } from "react";

import QuestionChip from "../QuestionChip";
import QuestionBox from "../VAPage/QuestionBox";

type Option = {
  id: any;
  label: string;
};

interface Props {
  label: string;
  description?: string;
  completed?: boolean;
  options: Option[];
  selectedOptionsIds?: any[];
  onClickOption: (option: Option) => void;
  selectedChipColor?: "primary" | "secondary";
  required?: boolean;
  // Controlled mode: parent drives open/close
  isOpen?: boolean;
  onRequestOpen?: () => void;
  onCollapse?: () => void;
  error?: string;
}

const MultiChipSelect = ({
  label,
  description,
  completed,
  options,
  selectedOptionsIds,
  onClickOption,
  selectedChipColor = "primary",
  required = false,
  isOpen,
  onRequestOpen,
  onCollapse,
  error,
}: Props) => {
  const [filled, setFilled] = useState(completed);

  const isControlled = isOpen !== undefined;
  const isExpanded = isControlled ? isOpen : !filled;

  const handleClick = !isExpanded
    ? isControlled
      ? onRequestOpen
      : () => setFilled(false)
    : undefined;

  const handleBlur = isExpanded
    ? (e: React.FocusEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node) && completed) {
          if (isControlled) {
            onCollapse?.();
          } else {
            setFilled(true);
          }
        }
      }
    : undefined;

  return (
    <Box onClick={handleClick} onBlur={handleBlur}>
      <QuestionBox
        error={error}
        tabIndex={-1}
        styles={!isExpanded ? { cursor: "pointer", p: 2 } : { p: 2 }}
      >
        <Box
          display="flex"
          justifyContent={"space-between"}
          alignItems="center"
          gap={2}
        >
          <Typography sx={{ mb: 2 }}>
            {label} {required ? "*" : ""}
          </Typography>

          {!isExpanded && completed && (
            <Box
              component="i"
              className="fa-regular fa-check"
              sx={{ color: "green.main" }}
            />
          )}
        </Box>
        {isExpanded && (
          <Box>
            <Typography variant="caption" color="textSecondary" paragraph>
              {description}
            </Typography>
          </Box>
        )}
        {isExpanded && (
          <Box
            display="flex"
            gap={1}
            flexWrap="wrap"
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((option: Option) => (
              <QuestionChip
                key={option?.id}
                isSelected={Boolean(
                  selectedOptionsIds &&
                    selectedOptionsIds.indexOf(option.id) > -1,
                )}
                label={option.label}
                onClick={(e) => {
                  e.stopPropagation();
                  onClickOption(option);
                }}
                color={selectedChipColor}
              />
            ))}
          </Box>
        )}
        {!isExpanded && completed && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {options
              .filter(
                (option: Option) =>
                  (selectedOptionsIds || []).indexOf(option.id) > -1,
              )
              .map((option: Option) => (
                <QuestionChip
                  key={option?.id}
                  isSelected={true}
                  label={option.label}
                  color={selectedChipColor}
                />
              ))}
          </Box>
        )}
      </QuestionBox>
    </Box>
  );
};

export default MultiChipSelect;
